import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from './use-toast';
import { Button } from './button';
import { Avatar, AvatarImage, AvatarFallback } from './avatar';
import { Textarea } from './textarea';
import { ScrollArea } from './scroll-area';
import { 
  X, Download, Share2, MessageCircle, Send, MapPin, 
  ChevronLeft, ChevronRight, Maximize2, Minimize2,
  Smile, Image as ImageIcon, Info, ZoomIn, ZoomOut, Play, Pause, RotateCw, Tag,
  AlertCircle, Keyboard, Sparkles, SlidersHorizontal, RefreshCw
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { hr, enUS } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import EmojiPicker from 'emoji-picker-react';
import { useResponsive } from '@/hooks/useResponsive';
import { generateSmartCommentsForPost } from '@/services/aiAssistantService';

export type LightboxMediaType = 'image' | 'video' | 'gif';

export type CinemaFilter = 'normal' | 'vibrant' | 'noir' | 'warm' | 'cool' | 'vintage';

export const CINEMA_FILTERS: { id: CinemaFilter; label: string; filterCss: string }[] = [
  { id: 'normal', label: 'Normal', filterCss: 'none' },
  { id: 'vibrant', label: 'Vibrant', filterCss: 'saturate(1.4) contrast(1.1)' },
  { id: 'noir', label: 'Noir', filterCss: 'grayscale(1) contrast(1.2)' },
  { id: 'warm', label: 'Warm', filterCss: 'sepia(0.28) saturate(1.25) hue-rotate(-10deg)' },
  { id: 'cool', label: 'Cool', filterCss: 'saturate(1.1) hue-rotate(15deg) contrast(1.05)' },
  { id: 'vintage', label: 'Vintage', filterCss: 'sepia(0.45) contrast(0.95) brightness(1.05)' },
];

export interface NormalizedMediaItem {
  id: string;
  url: string;
  type: LightboxMediaType;
  thumbnail?: string;
}

export interface Comment {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string | null;
  content: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video' | 'gif';
  createdAt: string;
  reactions?: Reaction[];
  replies?: Comment[];
}

export interface Reaction {
  id: string;
  userId: string;
  userName: string;
  type: 'like' | 'love' | 'laugh' | 'wow' | 'sad' | 'angry';
  timestamp: string;
}

export interface Location {
  lat: number;
  lng: number;
  address: string;
}

export interface AdvancedMediaLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  media?: (NormalizedMediaItem | string | Record<string, any>)[];
  initialIndex?: number;
  backgroundAudioUrl?: string;
  autoPlayBackgroundAudio?: boolean;
  postId?: string;
  authorId?: string;
  authorName?: string;
  authorPhoto?: string | null;
  createdAt?: string;
  content?: string;
  location?: Location | null;
  taggedUsers?: string[];
  reactions?: Reaction[];
  comments?: Comment[];
  onReactionAdd?: (type: string) => void;
  onCommentAdd?: (comment: string, mediaUrl?: string, mediaType?: string) => void;
  onLocationAdd?: (location: Location) => void;
  onTagUser?: (userId: string) => void;
  onUpdate?: (data: any) => void;
  initialTime?: number;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onEnded?: () => void;
}

function safeUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'id-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 11);
}

function sanitizeMediaUrl(url: unknown): string {
  if (typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^(javascript|vbscript):/i.test(trimmed)) {
    return '';
  }
  return trimmed;
}

function inferMediaType(url: string, fallback: LightboxMediaType = 'image'): LightboxMediaType {
  const lower = String(url || '').toLowerCase();
  if (/\.(mp4|webm|mov|m4v|ogg|ogv|avi|mkv)(\?|$)/.test(lower)) return 'video';
  if (/\.gif(\?|$)/.test(lower) || lower.includes('.giphy.com') || lower.includes('tenor.com') || lower.includes('/gif/')) return 'gif';
  if (/\.(jpg|jpeg|png|webp|bmp|avif|heic|heif|svg)(\?|$)/.test(lower)) return 'image';
  return fallback;
}

function normalizeMediaList(rawList: unknown): NormalizedMediaItem[] {
  if (!rawList) return [];
  const list = Array.isArray(rawList) ? rawList : [rawList];
  const results: NormalizedMediaItem[] = [];

  list.forEach((item, index) => {
    if (!item) return;

    if (typeof item === 'string') {
      const cleanUrl = sanitizeMediaUrl(item);
      if (cleanUrl) {
        results.push({
          id: `media-${index}`,
          url: cleanUrl,
          type: inferMediaType(cleanUrl),
          thumbnail: cleanUrl,
        });
      }
      return;
    }

    if (typeof item === 'object') {
      const obj = item as Record<string, any>;
      const rawUrl = obj.url || obj.mediaUrl || obj.src || obj.link || obj.downloadUrl || obj.uri || '';
      const cleanUrl = sanitizeMediaUrl(rawUrl);
      if (!cleanUrl) return;

      const rawType: LightboxMediaType = (obj.type === 'gif' || obj.mediaType === 'gif')
        ? 'gif'
        : (obj.type === 'video' || obj.mediaType === 'video')
        ? 'video'
        : (obj.type === 'image' || obj.mediaType === 'image')
        ? (/\.gif(\?|$)/i.test(cleanUrl) ? 'gif' : 'image')
        : inferMediaType(cleanUrl);

      const rawThumb = obj.thumbnail || obj.thumbnailUrl || obj.thumb || cleanUrl;

      results.push({
        id: String(obj.id || `media-${index}`),
        url: cleanUrl,
        type: rawType,
        thumbnail: sanitizeMediaUrl(rawThumb),
      });
    }
  });

  return results;
}

function safeFormatDistanceToNow(value: unknown, opts?: Parameters<typeof formatDistanceToNow>[1]): string {
  try {
    if (!value) return 'Just now';
    const date = value instanceof Date ? value : new Date(value as any);
    if (Number.isNaN(date.getTime())) return 'Just now';
    return formatDistanceToNow(date, opts || {});
  } catch {
    return 'Just now';
  }
}

const CONTROLS_AUTO_HIDE_MS = 3000;

export function AdvancedMediaLightbox({
  isOpen,
  onClose,
  media = [],
  initialIndex = 0,
  backgroundAudioUrl,
  autoPlayBackgroundAudio = false,
  postId = '',
  authorId = '',
  authorName = '',
  authorPhoto = null,
  createdAt = new Date().toISOString(),
  content = '',
  location = null,
  taggedUsers = [],
  reactions = [],
  comments = [],
  onReactionAdd,
  onCommentAdd,
  onLocationAdd,
  onTagUser,
  onUpdate,
  initialTime,
  onTimeUpdate,
  onEnded
}: AdvancedMediaLightboxProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const { isMobile } = useResponsive();

  const [resumedBannerVisible, setResumedBannerVisible] = useState(false);
  const lightboxVideoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (initialTime && initialTime > 2) {
      setResumedBannerVisible(true);
    } else {
      setResumedBannerVisible(false);
    }
  }, [initialTime, isOpen]);

  // Normalize media items safely
  const mediaItems = useMemo(() => normalizeMediaList(media), [media]);

  const [currentIndex, setCurrentIndex] = useState(() => {
    return Math.min(Math.max(initialIndex || 0, 0), Math.max(0, (mediaItems?.length || 1) - 1));
  });

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [commentMediaUrl, setCommentMediaUrl] = useState('');
  const [commentMediaType, setCommentMediaType] = useState<'image' | 'video' | 'gif' | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [localReactions, setLocalReactions] = useState<Reaction[]>(() => Array.isArray(reactions) ? reactions : []);
  const [localComments, setLocalComments] = useState<Comment[]>(() => Array.isArray(comments) ? comments : []);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [ambientGlowEnabled, setAmbientGlowEnabled] = useState(true);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [activeFilter, setActiveFilter] = useState<CinemaFilter>('normal');
  const [showFilterPicker, setShowFilterPicker] = useState(false);
  const [aiComments, setAiComments] = useState<string[]>([]);
  const [loadingAiComments, setLoadingAiComments] = useState(false);
  const [showAiComments, setShowAiComments] = useState(false);

  // Zoom, pan & slideshow
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isSlideshow, setIsSlideshow] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backgroundAudioRef = useRef<HTMLAudioElement>(null);
  const slideshowTimerRef = useRef<NodeJS.Timeout | null>(null);
  const controlsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const lastTapRef = useRef<number>(0);

  // Safe calculated index guaranteed within bounds
  const safeIndex = Math.min(Math.max(currentIndex || 0, 0), Math.max(0, (mediaItems?.length || 1) - 1));
  const currentMedia = mediaItems[safeIndex];
  const safeMediaUrl = currentMedia?.url || '';
  const currentMediaType = currentMedia?.type || 'image';

  // Keep state refs for keyboard handler
  const stateRef = useRef({
    isOpen,
    showEmojiPicker,
    showComments,
    showInfo,
    isFullscreen,
    isMobile,
    zoom,
    mediaCount: mediaItems.length,
    currentIndex: safeIndex,
    showShortcuts,
    ambientGlowEnabled,
    activeFilter,
    showFilterPicker,
  });

  useEffect(() => {
    stateRef.current = {
      isOpen,
      showEmojiPicker,
      showComments,
      showInfo,
      isFullscreen,
      isMobile,
      zoom,
      mediaCount: mediaItems.length,
      currentIndex: safeIndex,
      showShortcuts,
      ambientGlowEnabled,
      activeFilter,
      showFilterPicker,
    };
  });

  // Non-destructive scroll lock that preserves exact window scroll position
  useEffect(() => {
    if (!isOpen) return;
    const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    const originalOverflow = document.body.style.overflow;
    
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow && originalOverflow !== 'hidden' ? originalOverflow : '';
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: currentScrollY, behavior: 'instant' as ScrollBehavior });
      }
    };
  }, [isOpen]);

  // Sync state on open or post switch
  useEffect(() => {
    if (isOpen) {
      const validIndex = Math.min(Math.max(initialIndex || 0, 0), Math.max(0, (mediaItems?.length || 1) - 1));
      setCurrentIndex(validIndex);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setControlsVisible(true);
      setShowComments(!isMobile);
      setCommentText('');
      setCommentMediaUrl('');
      setCommentMediaType(null);
      setReplyingTo(null);
      setShowEmojiPicker(false);
      setShowInfo(false);
      setIsSlideshow(false);
      setImageLoadError(false);
      setShowFilterPicker(false);
      setShowAiComments(false);
      if (Array.isArray(reactions)) setLocalReactions(reactions);
      if (Array.isArray(comments)) setLocalComments(comments);
    }
  }, [isOpen, postId, initialIndex]);

  useEffect(() => {
    if (Array.isArray(reactions)) setLocalReactions(reactions);
    if (Array.isArray(comments)) setLocalComments(comments);
  }, [reactions, comments]);

  useEffect(() => {
    setImageLoadError(false);
  }, [safeIndex]);

  const fetchAiComments = useCallback(async () => {
    setLoadingAiComments(true);
    setShowAiComments(true);
    try {
      const suggestions = await generateSmartCommentsForPost(content || '', authorName || '');
      setAiComments(suggestions);
    } catch {
      setAiComments([
        'Great perspective, thanks for sharing! 🙌',
        'Really interesting, would love to hear more!',
        'Totally agree with this! 💯',
      ]);
    } finally {
      setLoadingAiComments(false);
    }
  }, [content, authorName]);

  const goToIndex = useCallback((targetIndex: number) => {
    if (mediaItems.length <= 0) return;
    const nextIdx = (targetIndex + mediaItems.length) % mediaItems.length;
    setCurrentIndex(nextIdx);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageLoadError(false);
  }, [mediaItems.length]);

  const handlePrevious = useCallback(() => {
    if (mediaItems.length <= 1) return;
    goToIndex(safeIndex - 1);
  }, [safeIndex, goToIndex, mediaItems.length]);

  const handleNext = useCallback(() => {
    if (mediaItems.length <= 1) return;
    goToIndex(safeIndex + 1);
  }, [safeIndex, goToIndex, mediaItems.length]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => {
    setZoom(prev => {
      const next = Math.max(prev - 0.25, 0.5);
      if (next <= 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };
  const handleZoomReset = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // Slideshow
  useEffect(() => {
    if (isSlideshow && mediaItems.length > 1) {
      slideshowTimerRef.current = setInterval(() => {
        goToIndex(safeIndex + 1);
      }, 3000);
    }
    return () => {
      if (slideshowTimerRef.current) clearInterval(slideshowTimerRef.current);
    };
  }, [isSlideshow, safeIndex, mediaItems.length, goToIndex]);

  // Controls auto-hide helper
  const clearControlsTimer = useCallback(() => {
    if (controlsTimerRef.current) {
      clearTimeout(controlsTimerRef.current);
      controlsTimerRef.current = null;
    }
  }, []);

  const pingControls = useCallback(() => {
    setControlsVisible(true);
    clearControlsTimer();
    if (!isMobile) {
      controlsTimerRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, CONTROLS_AUTO_HIDE_MS);
    }
  }, [clearControlsTimer, isMobile]);

  useEffect(() => {
    if (!isOpen) {
      clearControlsTimer();
      return;
    }
    pingControls();
    return () => clearControlsTimer();
  }, [isOpen, safeIndex, isFullscreen, pingControls, clearControlsTimer]);

  // Stable Global Keyboard Listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!stateRef.current.isOpen) return;

      const target = e.target as HTMLElement | null;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        Boolean(target?.isContentEditable) ||
        Boolean(target?.closest('input, textarea, [contenteditable="true"], .EmojiPickerReact'));

      if (isTyping) {
        if (e.key === 'Escape') {
          if (stateRef.current.showEmojiPicker) {
            setShowEmojiPicker(false);
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          target?.blur();
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        return;
      }

      pingControls();

      if (e.key === 'Escape') {
        if (stateRef.current.showFilterPicker) {
          setShowFilterPicker(false);
          return;
        }
        if (stateRef.current.showShortcuts) {
          setShowShortcuts(false);
          return;
        }
        if (stateRef.current.showEmojiPicker) {
          setShowEmojiPicker(false);
          return;
        }
        if (stateRef.current.isMobile && stateRef.current.showComments) {
          setShowComments(false);
          return;
        }
        if (stateRef.current.showInfo) {
          setShowInfo(false);
          return;
        }
        if (stateRef.current.isFullscreen) {
          setIsFullscreen(false);
          return;
        }
        onClose();
        return;
      }

      if (e.key === '?' || e.key === '/') {
        setShowShortcuts(prev => !prev);
        return;
      }
      if (e.key === 't' || e.key === 'T') {
        const filterIds: CinemaFilter[] = ['normal', 'vibrant', 'noir', 'warm', 'cool', 'vintage'];
        setActiveFilter(prev => {
          const nextIdx = (filterIds.indexOf(prev) + 1) % filterIds.length;
          return filterIds[nextIdx];
        });
        return;
      }
      if (e.key === 'g' || e.key === 'G') {
        setAmbientGlowEnabled(prev => !prev);
        return;
      }
      if (e.key === 'c' || e.key === 'C') {
        setShowComments(prev => !prev);
        return;
      }
      if (e.key === 'i' || e.key === 'I') {
        setShowInfo(prev => !prev);
        return;
      }
      if (e.key === ' ' || e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        if (mediaItems.length > 1) {
          setIsSlideshow(prev => !prev);
        }
        return;
      }

      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'f' || e.key === 'F') setIsFullscreen(prev => !prev);
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-' || e.key === '_') handleZoomOut();
      if (e.key === '0') handleZoomReset();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, handlePrevious, handleNext, pingControls]);

  // Touch Swipe & Double-Tap handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    pingControls();
    if (e.touches.length === 1) {
      touchStartXRef.current = e.touches[0].clientX;
      touchStartYRef.current = e.touches[0].clientY;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStartXRef.current;
    const endY = e.changedTouches[0]?.clientY ?? touchStartYRef.current;
    const diffX = touchStartXRef.current - endX;
    const diffY = touchStartYRef.current - endY;

    touchStartXRef.current = null;
    touchStartYRef.current = null;

    if (zoom <= 1.1 && Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY) * 1.3) {
      if (diffX > 0) handleNext();
      else handlePrevious();
    }
  };

  const handleMediaTap = (e: React.MouseEvent) => {
    e.stopPropagation();
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      setZoom(prev => {
        if (prev > 1) {
          setPosition({ x: 0, y: 0 });
          return 1;
        }
        return 2;
      });
      lastTapRef.current = 0;
      return;
    }
    lastTapRef.current = now;
    pingControls();
  };

  // Drag to pan when zoomed
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    pingControls();
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      setZoom(prev => {
        const next = Math.max(0.5, Math.min(3, prev + delta));
        if (next <= 1) setPosition({ x: 0, y: 0 });
        return next;
      });
    }
  };

  // Background Audio
  useEffect(() => {
    const audioEl = backgroundAudioRef.current;
    if (!audioEl) return;

    if (!isOpen || !backgroundAudioUrl) {
      audioEl.pause();
      audioEl.currentTime = 0;
      return;
    }

    if (!autoPlayBackgroundAudio) return;

    const playPromise = audioEl.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, [isOpen, backgroundAudioUrl, autoPlayBackgroundAudio]);

  if (!isOpen) return null;

  const locale = i18n?.language === 'hr' ? hr : enUS;
  const safeAuthorName = typeof authorName === 'string' && authorName.trim().length > 0
    ? authorName
    : t('common.unknown', 'Unknown');

  // Handle Download with CORS fallback
  const handleDownload = async () => {
    if (!safeMediaUrl) return;
    const downloadFilename = `media-${Date.now()}.${currentMediaType === 'video' ? 'mp4' : 'jpg'}`;

    try {
      const response = await fetch(safeMediaUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Fetch failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        if (document.body.contains(a)) document.body.removeChild(a);
      }, 200);

      toast({
        title: t('common.success', 'Success'),
        description: t('media.downloaded', 'Media downloaded successfully'),
      });
    } catch {
      try {
        const a = document.createElement('a');
        a.href = safeMediaUrl;
        a.download = downloadFilename;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          if (document.body.contains(a)) document.body.removeChild(a);
        }, 200);

        toast({
          title: t('common.success', 'Success'),
          description: t('media.openedDownload', 'Download started'),
        });
      } catch {
        toast({
          title: t('common.error', 'Error'),
          description: t('media.downloadFailed', 'Failed to download media'),
          variant: 'destructive',
        });
      }
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Post by ${safeAuthorName}`,
          text: content || '',
          url: window.location.href,
        });
      } catch {
        // cancelled
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: t('common.success', 'Success'),
        description: t('media.linkCopied', 'Link copied to clipboard'),
      });
    }
  };

  // Reactions & Comments Logic
  const handleReaction = (type: Reaction['type']) => {
    const existing = (localReactions || []).find(r => r.userId === user?.uid);
    let nextReactions: Reaction[];

    if (existing && existing.type === type) {
      nextReactions = (localReactions || []).filter(r => r.userId !== user?.uid);
    } else if (existing) {
      nextReactions = (localReactions || []).map(r =>
        r.userId === user?.uid ? { ...r, type, timestamp: new Date().toISOString() } : r
      );
    } else {
      nextReactions = [
        ...(localReactions || []),
        {
          id: safeUUID(),
          userId: user?.uid || '',
          userName: user?.displayName || 'Anonymous',
          type,
          timestamp: new Date().toISOString()
        }
      ];
    }

    setLocalReactions(nextReactions);
    onReactionAdd?.(type);
    onUpdate?.({ reactions: nextReactions });
  };

  const handleCommentSubmit = () => {
    if (!commentText.trim() && !commentMediaUrl) return;

    const newComment: Comment = {
      id: safeUUID(),
      userId: user?.uid || '',
      userName: user?.displayName || 'Anonymous',
      userPhoto: user?.photoURL || null,
      content: commentText.trim(),
      mediaUrl: commentMediaUrl || undefined,
      mediaType: commentMediaType || undefined,
      createdAt: new Date().toISOString(),
      reactions: [],
      replies: []
    };

    let nextComments: Comment[];
    if (replyingTo) {
      nextComments = (localComments || []).map(comment => {
        if (comment.id === replyingTo) {
          return { ...comment, replies: [...(comment.replies || []), newComment] };
        }
        return comment;
      });
    } else {
      nextComments = [newComment, ...(localComments || [])];
    }

    setLocalComments(nextComments);
    onCommentAdd?.(commentText.trim(), commentMediaUrl || undefined, commentMediaType || undefined);
    onUpdate?.({ comments: nextComments });

    setCommentText('');
    setCommentMediaUrl('');
    setCommentMediaType(null);
    setReplyingTo(null);
  };

  const handleMediaUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      toast({
        title: t('common.error', 'Error'),
        description: t('media.fileTooLarge', 'File is too large. Maximum size is 15MB.'),
        variant: 'destructive',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const isVid = file.type.startsWith('video');
    const isImg = file.type.startsWith('image');
    if (!isVid && !isImg) {
      toast({
        title: t('common.error', 'Error'),
        description: t('media.invalidFileType', 'Please select a valid image or video file.'),
        variant: 'destructive',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setCommentMediaUrl(url);
      setCommentMediaType(isVid ? 'video' : 'image');
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const userReaction = (localReactions || []).find(r => r.userId === user?.uid);
  const totalReactions = (localReactions || []).length;
  const totalComments = (localComments || []).length;

  // Sidebar content (Comments & Details)
  const sidebarContent = (
    <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
        <h3 className="font-bold text-lg bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          {showInfo ? t('media.details', 'Details') : t('media.activity', 'Activity')}
        </h3>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowInfo(!showInfo)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800"
          >
            {showInfo ? t('media.showActivity', 'Activity') : t('media.showDetails', 'Details')}
          </Button>
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowComments(false)}
              className="h-8 w-8 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {showInfo ? (
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4 text-sm">
            {location && (
              <div className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1.5 font-semibold text-blue-600 dark:text-blue-400">
                  <MapPin className="w-4 h-4" />
                  <span>{t('media.location', 'Location')}</span>
                </div>
                <p className="text-slate-600 dark:text-slate-300">{location.address}</p>
              </div>
            )}

            {Array.isArray(taggedUsers) && taggedUsers.length > 0 && (
              <div className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2 font-semibold text-blue-600 dark:text-blue-400">
                  <Tag className="w-4 h-4" />
                  <span>{t('media.tagged', 'Tagged')}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {taggedUsers.map((uid, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full text-xs font-medium">
                      @{uid}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2">
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-slate-500">{t('media.type', 'Type')}</span>
                <span className="font-semibold uppercase">{currentMediaType}</span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-slate-500">{t('media.index', 'Item')}</span>
                <span className="font-semibold">{safeIndex + 1} / {mediaItems.length || 1}</span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-slate-500">{t('media.posted', 'Posted')}</span>
                <span>{safeFormatDistanceToNow(createdAt, { addSuffix: true, locale })}</span>
              </div>
            </div>
          </div>
        </ScrollArea>
      ) : (
        <>
          <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs text-slate-500 shrink-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-slate-700 dark:text-slate-300">{totalReactions}</span>
              <span>reactions</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-slate-700 dark:text-slate-300">{totalComments}</span>
              <span>comments</span>
            </div>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {(localComments || []).map((comment) => (
                <div key={comment.id} className="flex gap-2.5">
                  <Avatar className="w-7 h-7 shrink-0">
                    <AvatarImage src={comment.userPhoto || undefined} />
                    <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-[10px]">
                      {(comment.userName || 'U').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-3.5 py-2">
                      <p className="font-semibold text-xs text-slate-900 dark:text-slate-100">{comment.userName || 'Anonymous'}</p>
                      <p className="text-xs mt-0.5 whitespace-pre-wrap break-words text-slate-800 dark:text-slate-200">{comment.content}</p>
                      {comment.mediaUrl && (
                        <div className="mt-2 rounded-lg overflow-hidden max-w-xs">
                          {comment.mediaType === 'video' ? (
                            <video src={comment.mediaUrl} controls className="max-w-full max-h-36 rounded-lg" />
                          ) : (
                            <img src={comment.mediaUrl} alt="" className="max-w-full max-h-36 rounded-lg object-cover" />
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 ml-2 text-[11px] text-slate-400">
                      <span>{safeFormatDistanceToNow(comment.createdAt, { addSuffix: true, locale })}</span>
                      <button 
                        onClick={() => {
                          setReplyingTo(comment.id);
                          commentInputRef.current?.focus();
                        }}
                        className="hover:text-blue-500 font-medium"
                      >
                        Reply
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {(!localComments || localComments.length === 0) && (
                <div className="text-center py-8 text-slate-400 text-xs">
                  <MessageCircle className="w-8 h-8 mx-auto mb-1.5 opacity-40" />
                  {t('media.noComments', 'No comments yet. Be the first to comment!')}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Reaction Bar */}
          <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-800 flex items-center justify-around bg-slate-50 dark:bg-slate-900 shrink-0">
            {[
              { type: 'like', icon: '👍' },
              { type: 'love', icon: '❤️' },
              { type: 'laugh', icon: '😂' },
              { type: 'wow', icon: '😮' },
              { type: 'sad', icon: '😢' },
              { type: 'angry', icon: '😠' }
            ].map(({ type, icon }) => (
              <button
                key={type}
                onClick={() => handleReaction(type as any)}
                className={cn(
                  "p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800 transition-transform active:scale-95 text-lg",
                  userReaction?.type === type && "bg-blue-100 dark:bg-blue-900/50"
                )}
                title={type}
              >
                {icon}
              </button>
            ))}
          </div>

          {/* Comment Input */}
          <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0 relative">
            {/* DeepSeek AI Quick Replies */}
            {showAiComments && (
              <div className="mb-2.5 p-2 rounded-xl bg-purple-50/80 dark:bg-purple-950/30 border border-purple-200/60 dark:border-purple-800/40">
                <div className="flex items-center justify-between text-[11px] font-medium text-purple-700 dark:text-purple-300 mb-1.5 px-0.5">
                  <span className="flex items-center gap-1.5 font-semibold">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                    DeepSeek AI Quick Replies
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={fetchAiComments}
                      disabled={loadingAiComments}
                      className="p-0.5 text-slate-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
                      title="Regenerate suggestions"
                    >
                      <RefreshCw className={cn("w-3 h-3", loadingAiComments && "animate-spin text-purple-600")} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAiComments(false)}
                      className="p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      title="Dismiss"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                {loadingAiComments ? (
                  <div className="flex items-center gap-1.5 text-[11px] text-purple-600 dark:text-purple-400 py-1">
                    <div className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-purple-600" />
                    Generating smart suggestions...
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    {aiComments.map((suggestion, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setCommentText(prev => (prev.trim() ? `${prev.trim()} ${suggestion}` : suggestion));
                          commentInputRef.current?.focus();
                        }}
                        className="text-left text-[11px] px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-800/90 text-slate-700 dark:text-slate-200 hover:bg-purple-100 dark:hover:bg-purple-900/40 border border-purple-200/50 dark:border-purple-800/50 transition-all hover:scale-[1.01] active:scale-[0.99] leading-snug shadow-sm"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {replyingTo && (
              <div className="flex items-center justify-between mb-2 px-2.5 py-1 bg-blue-50 dark:bg-blue-950/40 rounded-lg text-xs text-blue-600 dark:text-blue-400 font-medium">
                <span>Replying to comment</span>
                <button onClick={() => setReplyingTo(null)}><X className="w-3.5 h-3.5" /></button>
              </div>
            )}

            {commentMediaUrl && (
              <div className="relative mb-2 rounded-lg overflow-hidden border border-slate-300 dark:border-slate-700 max-w-[140px]">
                {commentMediaType === 'video' ? (
                  <video src={commentMediaUrl} className="w-full max-h-20 object-cover" />
                ) : (
                  <img src={commentMediaUrl} alt="" className="w-full max-h-20 object-cover" />
                )}
                <button
                  onClick={() => { setCommentMediaUrl(''); setCommentMediaType(null); }}
                  className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            <div className="flex items-end gap-2">
              <Avatar className="w-7 h-7 shrink-0">
                <AvatarImage src={user?.photoURL || undefined} />
                <AvatarFallback className="bg-blue-600 text-white text-xs">
                  {(user?.displayName || 'U').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 relative">
                <Textarea
                  ref={commentInputRef}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={t('media.writeComment', 'Write a comment...')}
                  className="min-h-[36px] max-h-24 text-xs resize-none pr-24 rounded-xl py-1.5 px-3 leading-snug"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleCommentSubmit();
                    }
                  }}
                />

                <div className="absolute right-1 bottom-1 flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (!showAiComments && aiComments.length === 0) {
                        fetchAiComments();
                      } else {
                        setShowAiComments(prev => !prev);
                      }
                    }}
                    className={cn(
                      "h-6 w-6 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/40",
                      showAiComments && "bg-purple-100 dark:bg-purple-900/50 text-purple-700"
                    )}
                    title="DeepSeek AI Quick Replies"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="h-6 w-6 text-slate-500 hover:text-slate-800"
                    title="Emoji"
                  >
                    <Smile className="w-3.5 h-3.5" />
                  </Button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={handleMediaUpload}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-6 w-6 text-slate-500 hover:text-slate-800"
                    title="Attach Media"
                  >
                    <ImageIcon className="w-3.5 h-3.5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleCommentSubmit}
                    disabled={!commentText.trim() && !commentMediaUrl}
                    className="h-6 w-6 text-blue-600 disabled:text-slate-300"
                    title="Send"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            {showEmojiPicker && (
              <div className="absolute bottom-14 right-2 z-50 shadow-2xl rounded-xl overflow-hidden">
                <EmojiPicker
                  onEmojiClick={(emojiData) => {
                    setCommentText(prev => prev + emojiData.emoji);
                    setShowEmojiPicker(false);
                    commentInputRef.current?.focus();
                  }}
                  width={280}
                  height={320}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // If no media items found, show safe fallback modal that can be closed
  if (mediaItems.length === 0 || !safeMediaUrl) {
    return createPortal(
      <div 
        role="dialog"
        aria-modal="true"
        aria-label="Media Lightbox"
        style={{ zIndex: 99999999 }}
        className="fixed inset-0 flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
        onClick={onClose}
      >
        <div 
          className="rounded-2xl border border-white/20 bg-slate-900 p-6 text-center text-white shadow-2xl max-w-sm w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <AlertCircle className="w-12 h-12 mx-auto text-amber-400 mb-3 opacity-90" />
          <p className="text-base font-semibold">{t('media.unavailable', 'Media is unavailable')}</p>
          <p className="text-xs text-white/60 mt-1 mb-5">{t('media.unavailableDesc', 'The selected media file could not be loaded.')}</p>
          <Button variant="default" className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        </div>
      </div>,
      // Portal to <html> so a `filter` on <body> (accessibility contrast/saturation)
      // cannot trap this fixed overlay into the document flow.
      document.documentElement
    );
  }

  const lightbox = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Media Lightbox"
      style={{ zIndex: 99999999 }}
      className="fixed inset-0 flex h-[100dvh] w-screen items-center justify-center bg-black select-none overflow-hidden"
      onClick={onClose}
      onMouseMove={pingControls}
    >
      {/* ALWAYS VISIBLE CLOSE BUTTON */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
        style={{ zIndex: 100000000 }}
        className="fixed top-4 right-4 flex items-center justify-center w-11 h-11 rounded-full bg-black/80 hover:bg-black text-white border border-white/40 backdrop-blur-xl shadow-2xl transition-all hover:scale-105 active:scale-95 cursor-pointer"
        title="Close (ESC)"
        aria-label="Close Lightbox"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Main Responsive Grid Container */}
      <div 
        className={cn(
          "flex w-full h-full relative transition-all duration-300",
          isFullscreen ? "p-0" : "max-w-[1600px] max-h-[100dvh]"
        )}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        {/* Media Center Viewport */}
        <div 
          className={cn(
            "relative flex-1 flex items-center justify-center min-w-0 h-full overflow-hidden bg-black"
          )}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
          onWheel={handleWheel}
        >
          {/* Author Card Info (Top-Left) */}
          <div
            className={cn(
              "absolute top-4 left-4 z-30 transition-opacity duration-200 pointer-events-auto",
              controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5 bg-black/80 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/20 shadow-xl max-w-[260px]">
              <Avatar className="w-8 h-8 ring-1 ring-white/40 shrink-0">
                <AvatarImage src={authorPhoto || undefined} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs font-bold">
                  {safeAuthorName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 pr-1">
                <p className="font-semibold text-white text-xs truncate drop-shadow">{safeAuthorName}</p>
                <p className="text-[10px] text-white/70 truncate">
                  {safeFormatDistanceToNow(createdAt, { addSuffix: true, locale })}
                </p>
              </div>
            </div>
          </div>

          {/* Top-Right Control Bar */}
          <div
            className={cn(
              "absolute top-4 right-16 z-30 transition-opacity duration-200 pointer-events-auto",
              controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1.5 bg-black/80 backdrop-blur-md rounded-full px-2.5 py-1.5 border border-white/20 shadow-xl">
              {currentMediaType === 'gif' && (
                <span className="inline-flex items-center rounded-md bg-purple-600 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow">
                  GIF
                </span>
              )}

              {(currentMediaType === 'image' || currentMediaType === 'gif') && !imageLoadError && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleZoomOut}
                    disabled={zoom <= 0.5}
                    className="h-7 w-7 text-white hover:bg-white/20 rounded-full"
                    title="Zoom Out (-)"
                  >
                    <ZoomOut className="w-3.5 h-3.5" />
                  </Button>
                  <span className="text-[11px] font-semibold text-white/90 px-1">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleZoomIn}
                    disabled={zoom >= 3}
                    className="h-7 w-7 text-white hover:bg-white/20 rounded-full"
                    title="Zoom In (+)"
                  >
                    <ZoomIn className="w-3.5 h-3.5" />
                  </Button>
                  {zoom !== 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleZoomReset}
                      className="h-7 w-7 text-white hover:bg-white/20 rounded-full"
                      title="Reset Zoom (0)"
                    >
                      <RotateCw className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <div className="w-px h-4 bg-white/20 mx-0.5" />
                </>
              )}

              {mediaItems.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsSlideshow(prev => !prev)}
                  className={cn(
                    "h-7 w-7 text-white hover:bg-white/20 rounded-full",
                    isSlideshow && "bg-blue-600 text-white"
                  )}
                  title={isSlideshow ? "Pause Slideshow" : "Play Slideshow"}
                >
                  {isSlideshow ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </Button>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowComments(!showComments)}
                className={cn(
                  "h-7 w-7 text-white hover:bg-white/20 rounded-full relative",
                  showComments && "bg-blue-600 text-white"
                )}
                title={showComments ? "Hide Comments" : "Show Comments"}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                {totalComments > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] font-bold rounded-full h-3 min-w-[12px] px-0.5 flex items-center justify-center ring-1 ring-black">
                    {totalComments}
                  </span>
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setAmbientGlowEnabled(prev => !prev)}
                className={cn(
                  "h-7 w-7 text-white hover:bg-white/20 rounded-full transition-colors",
                  ambientGlowEnabled && "text-cyan-400 bg-cyan-950/40 border border-cyan-500/30"
                )}
                title={ambientGlowEnabled ? "Disable Ambient Glow (G)" : "Enable Ambient Glow (G)"}
              >
                <Sparkles className="w-3.5 h-3.5" />
              </Button>

              {/* Cinema Image Filters Button */}
              {(currentMediaType === 'image' || currentMediaType === 'gif') && !imageLoadError && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowFilterPicker(prev => !prev)}
                  className={cn(
                    "h-7 w-7 text-white hover:bg-white/20 rounded-full transition-colors relative",
                    (showFilterPicker || activeFilter !== 'normal') && "text-amber-400 bg-amber-950/40 border border-amber-500/30"
                  )}
                  title={`Cinema Filters (T)${activeFilter !== 'normal' ? `: ${activeFilter}` : ''}`}
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  {activeFilter !== 'normal' && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 ring-1 ring-black" />
                  )}
                </Button>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowShortcuts(prev => !prev)}
                className={cn(
                  "h-7 w-7 text-white hover:bg-white/20 rounded-full transition-colors",
                  showShortcuts && "text-blue-400 bg-blue-950/40 border border-blue-500/30"
                )}
                title="Keyboard Shortcuts (?)"
              >
                <Keyboard className="w-3.5 h-3.5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowInfo(!showInfo)}
                className={cn(
                  "h-7 w-7 text-white hover:bg-white/20 rounded-full",
                  showInfo && "bg-blue-600 text-white"
                )}
                title="Details"
              >
                <Info className="w-3.5 h-3.5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsFullscreen(prev => !prev)}
                className="h-7 w-7 text-white hover:bg-white/20 rounded-full"
                title={isFullscreen ? "Exit Fullscreen (F)" : "Fullscreen (F)"}
              >
                {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleDownload}
                className="h-7 w-7 text-white hover:bg-white/20 rounded-full"
                title="Download"
              >
                <Download className="w-3.5 h-3.5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleShare}
                className="h-7 w-7 text-white hover:bg-white/20 rounded-full"
                title="Share"
              >
                <Share2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Floating Cinema Filter Preset Selector */}
          {showFilterPicker && (currentMediaType === 'image' || currentMediaType === 'gif') && !imageLoadError && (
            <div
              className="absolute top-16 right-16 z-40 flex items-center gap-1.5 p-1.5 rounded-2xl bg-black/85 backdrop-blur-xl border border-white/20 shadow-2xl transition-all"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-[10px] uppercase font-bold text-white/50 px-2 tracking-wider">
                Filter
              </span>
              {CINEMA_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={cn(
                    "px-2.5 py-1 rounded-xl text-xs font-medium transition-all",
                    activeFilter === filter.id
                      ? "bg-amber-500 text-black font-semibold shadow-md scale-105"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                >
                  {filter.label}
                </button>
              ))}
              <button
                onClick={() => setShowFilterPicker(false)}
                className="p-1 text-white/50 hover:text-white rounded-lg hover:bg-white/10 ml-0.5"
                title="Close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Navigation Arrows */}
          {mediaItems.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevious();
                }}
                className={cn(
                  "absolute left-4 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center",
                  "w-11 h-11 rounded-full bg-black/70 hover:bg-black/90 text-white border border-white/20 backdrop-blur-md shadow-2xl transition-all hover:scale-105 active:scale-95 cursor-pointer",
                  controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                title="Previous (←)"
                aria-label="Previous Media"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className={cn(
                  "absolute right-4 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center",
                  "w-11 h-11 rounded-full bg-black/70 hover:bg-black/90 text-white border border-white/20 backdrop-blur-md shadow-2xl transition-all hover:scale-105 active:scale-95 cursor-pointer",
                  controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                title="Next (→)"
                aria-label="Next Media"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Dynamic Cinema Ambient Glow */}
          {ambientGlowEnabled && safeMediaUrl && !imageLoadError && (
            <div 
              aria-hidden="true" 
              className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden opacity-35 select-none transition-opacity duration-700"
            >
              {currentMediaType === 'video' ? (
                <div className="w-[85vw] max-w-4xl h-[65vh] rounded-full bg-gradient-to-r from-blue-600/40 via-purple-600/40 to-cyan-500/40 blur-3xl animate-pulse" />
              ) : (
                <img
                  src={safeMediaUrl}
                  alt=""
                  className="w-3/4 max-w-3xl h-3/4 max-h-[70vh] object-cover scale-150 blur-3xl transition-all duration-700"
                  style={{
                    filter: CINEMA_FILTERS.find(f => f.id === activeFilter)?.filterCss !== 'none'
                      ? CINEMA_FILTERS.find(f => f.id === activeFilter)!.filterCss
                      : undefined,
                  }}
                  draggable={false}
                />
              )}
            </div>
          )}

          {/* Active Media Container - Click anywhere outside actual media dismisses */}
          <div 
            className="w-full h-full flex items-center justify-center p-4 sm:p-8 cursor-pointer relative z-10"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            {imageLoadError ? (
              <div 
                className="flex flex-col items-center justify-center text-center p-8 bg-slate-900/80 rounded-2xl border border-white/20 text-white max-w-sm cursor-default"
                onClick={(e) => e.stopPropagation()}
              >
                <AlertCircle className="w-10 h-10 text-rose-500 mb-2" />
                <p className="font-semibold text-sm">Failed to load media</p>
                <p className="text-xs text-white/60 mt-1 mb-4">The media file might be unavailable or removed.</p>
                <Button size="sm" variant="secondary" onClick={() => goToIndex(safeIndex + 1)}>
                  Next Item
                </Button>
              </div>
            ) : currentMediaType === 'video' ? (
              <div className="relative flex items-center justify-center max-w-full max-h-[82vh]">
                <video
                  ref={lightboxVideoRef}
                  key={safeMediaUrl}
                  src={safeMediaUrl}
                  controls
                  autoPlay
                  playsInline
                  className="max-w-full max-h-[82vh] w-auto h-auto object-contain rounded-xl shadow-2xl cursor-default"
                  onClick={(e) => e.stopPropagation()}
                  onError={() => setImageLoadError(true)}
                  onLoadedMetadata={(e) => {
                    if (initialTime && initialTime > 0 && e.currentTarget.duration > initialTime) {
                      e.currentTarget.currentTime = initialTime;
                      setResumedBannerVisible(true);
                    }
                  }}
                  onTimeUpdate={(e) => {
                    onTimeUpdate?.(e.currentTarget.currentTime, e.currentTarget.duration);
                  }}
                  onEnded={() => {
                    onEnded?.();
                  }}
                />
                {resumedBannerVisible && initialTime && initialTime > 2 && (
                  <div 
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-4 left-4 z-40 flex items-center gap-2.5 bg-black/85 backdrop-blur-md px-3.5 py-2 rounded-xl text-white text-xs border border-white/20 shadow-2xl animate-in fade-in slide-in-from-top-2"
                  >
                    <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                    <span>
                      Resumed from{' '}
                      <span className="font-semibold text-cyan-300">
                        {Math.floor(initialTime / 60)}:{(Math.floor(initialTime % 60)).toString().padStart(2, '0')}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (lightboxVideoRef.current) {
                          lightboxVideoRef.current.currentTime = 0;
                          lightboxVideoRef.current.play().catch(() => {});
                        }
                        setResumedBannerVisible(false);
                      }}
                      className="ml-1 text-cyan-300 hover:text-cyan-200 font-semibold underline underline-offset-2 transition-colors cursor-pointer"
                    >
                      Start Over
                    </button>
                    <button
                      type="button"
                      onClick={() => setResumedBannerVisible(false)}
                      className="ml-1 p-0.5 text-white/60 hover:text-white rounded transition-colors cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <img
                key={safeMediaUrl}
                src={safeMediaUrl}
                alt={content || "Media preview"}
                onClick={handleMediaTap}
                onError={() => setImageLoadError(true)}
                className="max-w-full max-h-[82vh] w-auto h-auto object-contain rounded-xl shadow-2xl transition-transform duration-100 cursor-zoom-in"
                style={{
                  transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                  filter: `drop-shadow(0 20px 40px rgba(0,0,0,0.6))${
                    CINEMA_FILTERS.find(f => f.id === activeFilter)?.filterCss && CINEMA_FILTERS.find(f => f.id === activeFilter)!.filterCss !== 'none'
                      ? ` ${CINEMA_FILTERS.find(f => f.id === activeFilter)!.filterCss}`
                      : ''
                  }`.trim(),
                }}
                draggable={false}
              />
            )}
          </div>

          {/* Bottom Captions & Thumbnails Strip */}
          <div
            className={cn(
              "absolute bottom-0 left-0 right-0 z-30 transition-opacity duration-200 pointer-events-auto",
              "bg-gradient-to-t from-black via-black/70 to-transparent pt-6 pb-4 px-4 sm:px-8",
              controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {content && (
              <p className="text-white text-xs sm:text-sm font-medium text-center max-w-2xl mx-auto mb-3 line-clamp-2 drop-shadow-md">
                {content}
              </p>
            )}

            {mediaItems.length > 1 && (
              <div className="flex items-center justify-center gap-2 overflow-x-auto py-1 max-w-xl mx-auto">
                {mediaItems.map((item, idx) => {
                  const isSelected = idx === safeIndex;
                  return (
                    <button
                      key={item.id || idx}
                      onClick={() => goToIndex(idx)}
                      className={cn(
                        "relative w-12 h-12 rounded-lg overflow-hidden shrink-0 border-2 transition-all cursor-pointer",
                        isSelected
                          ? "border-blue-500 ring-2 ring-blue-400/80 scale-105 shadow-lg"
                          : "border-white/30 opacity-60 hover:opacity-100 hover:border-white/70"
                      )}
                    >
                      {item.type === 'video' ? (
                        <div className="w-full h-full bg-slate-900 flex items-center justify-center">
                          <Play className="w-3.5 h-3.5 text-white fill-current" />
                        </div>
                      ) : item.type === 'gif' ? (
                        <div className="relative w-full h-full">
                          <img src={item.thumbnail || item.url} alt="" className="w-full h-full object-cover" />
                          <span className="absolute bottom-0.5 right-0.5 rounded bg-purple-700/90 px-1 py-0.2 text-[7px] font-extrabold text-white">
                            GIF
                          </span>
                        </div>
                      ) : (
                        <img src={item.thumbnail || item.url} alt="" className="w-full h-full object-cover" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Desktop Sidebar */}
        {!isFullscreen && !isMobile && showComments && (
          <div 
            className="w-[360px] shrink-0 h-full border-l border-slate-800 shadow-2xl z-40 bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            {sidebarContent}
          </div>
        )}

        {/* Mobile Slide-Up Bottom Drawer */}
        {isMobile && showComments && (
          <div
            className="fixed inset-x-0 bottom-0 h-[80vh] z-50 rounded-t-3xl bg-white dark:bg-slate-900 shadow-2xl border-t border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto my-2 shrink-0" />
            <div className="flex-1 overflow-hidden">
              {sidebarContent}
            </div>
          </div>
        )}
      </div>

      {backgroundAudioUrl && (
        <audio
          ref={backgroundAudioRef}
          src={backgroundAudioUrl}
          autoPlay={autoPlayBackgroundAudio}
          loop
          preload="metadata"
          className="hidden"
        />
      )}

      {/* Keyboard Shortcuts Cheat Sheet Modal */}
      {showShortcuts && (
        <div 
          role="dialog"
          aria-modal="true"
          aria-label="Keyboard Shortcuts"
          className="fixed inset-0 z-[100000001] flex items-center justify-center bg-black/75 backdrop-blur-md p-4 animate-fade-in"
          onClick={() => setShowShortcuts(false)}
        >
          <div 
            className="w-full max-w-md rounded-2xl border border-white/20 bg-slate-900/95 p-6 text-white shadow-2xl backdrop-blur-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Keyboard className="w-5 h-5 text-blue-400" />
                <h3 className="text-base font-semibold">Lightbox Shortcuts</h3>
              </div>
              <button 
                onClick={() => setShowShortcuts(false)}
                className="p-1 rounded-full text-white/60 hover:text-white hover:bg-white/10"
                aria-label="Close Shortcuts"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Navigate</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">← / →</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Fullscreen</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">F</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Zoom In / Out</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">+ / -</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Reset Zoom</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">0</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Slideshow</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">Space</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Comments</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">C</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Details</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">I</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Ambient Glow</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">G</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Cinema Filter</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">T</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Shortcuts Help</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">?</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                <span className="text-white/70">Close</span>
                <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white font-bold">ESC</span>
              </div>
            </div>

            <Button 
              className="mt-5 w-full bg-blue-600 hover:bg-blue-700 text-white text-xs h-8"
              onClick={() => setShowShortcuts(false)}
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Portal to <html> (not <body>) so the overlay stays viewport-fixed even if a
  // `filter`/`transform` is applied to <body>, which would otherwise become the
  // containing block for position:fixed and pin the lightbox to the document top.
  return createPortal(lightbox, document.documentElement);
}
